import { useMutation } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'

import { authAPI } from '@/api/new-api'
import { useSnackbar } from '@/helpers/hooks/useSnackbar'
import { downloadHref } from '@/utils'

export interface GetVisitorParkingSessionsExportRequest {
  buildingId: number
  from?: number
  to?: number
}

export interface DownloadSessionsRequest extends GetVisitorParkingSessionsExportRequest {
  fileName: string
}

export const getVisitorParkingSessionsExport = async (
  data: GetVisitorParkingSessionsExportRequest,
) => {
  const response = await authAPI.get<Blob>(`/visitor-parking/sessions/export`, {
    params: data,
    responseType: 'blob',
  })
  return response.data
}

export const useGetVisitorParkingSessionsExport = () => {
  const objectUrlRef = useRef<string | null>(null)

  const { displaySnackbar } = useSnackbar()

  const mutation = useMutation({
    mutationFn: async ({ fileName, ...rest }: DownloadSessionsRequest) => {
      const blob = await getVisitorParkingSessionsExport(rest)
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current)
      objectUrlRef.current = URL.createObjectURL(blob)
      downloadHref(objectUrlRef.current, fileName)
      return objectUrlRef.current
    },
    onError: (error) => {
      displaySnackbar(error.message, true)
    },
  })

  // NOTE: clean up the URL object (created after mutation) when `useGetVisitorParkingSessionsExport` unmounts
  useEffect(() => {
    return () => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current)
    }
  }, [])

  return mutation
}
